import { useEffect } from "react";

export interface ShortcutHandlers {
  onSubmit?: () => void;
  onClear?: () => void;
  onCopy?: () => void;
  onSave?: () => void;
  enabled?: boolean;
}

const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

export const modifierKey = isMac ? "⌘" : "Ctrl";

export function formatShortcut(parts: string[]): string {
  return parts.join(isMac ? "" : "+");
}

export function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "SELECT" || (tag === "TEXTAREA" && (t as HTMLTextAreaElement).type !== "button") || t.isContentEditable;
}

export function useKeyboardShortcuts({ onSubmit, onClear, onCopy, onSave, enabled = true }: ShortcutHandlers) {
  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;

      // Ctrl/Cmd + Enter → submit (works everywhere, including textarea)
      if (e.key === "Enter" && !e.shiftKey && onSubmit) {
        e.preventDefault();
        onSubmit();
        return;
      }

      // Ctrl/Cmd + K → clear input (skip when typing in a field — preserves native behavior)
      if ((e.key === "k" || e.key === "K") && !e.shiftKey && onClear) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        onClear();
        return;
      }

      // Ctrl/Cmd + Shift + C → copy output (skip when focus is in an editable field)
      if ((e.key === "c" || e.key === "C") && e.shiftKey && onCopy) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        onCopy();
        return;
      }

      // Ctrl/Cmd + S → save (skip in editable fields so drafts aren't fighting the shortcut)
      if ((e.key === "s" || e.key === "S") && !e.shiftKey && onSave) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        onSave();
        return;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onSubmit, onClear, onCopy, onSave, enabled]);
}
